import user from '@/stores/user';
import storage from '@/utils/storage';
import { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';

const AUTH_TOKEN_KEY = 'auth-token';

interface AuthTokenData {
    token: string;
    userId: string;
    expiry: number;
    address?: string;
}

export default function useSignature() {
    const { setUserId } = user;
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [authToken, setAuthToken] = useState<string | null>(null);
    const { isConnected, address } = useAccount();

    // Check if the token is valid
    const isTokenValid = (): boolean => {
        const storedData = storage.get(AUTH_TOKEN_KEY) as
            | AuthTokenData
            | undefined;
        if (!storedData || !storedData.token || !storedData.expiry) {
            return false;
        }

        // Check if the token is expired
        const isNotExpired = storedData.expiry > Date.now();

        // Check if the address matches
        let isAddressMatch = true; // Default to true

        // Only compare addresses when there is a stored address and a connected address
        if (storedData.address && address) {
            isAddressMatch =
                storedData.address.toLowerCase() === address.toLowerCase();
        }

        // Return true only if the token is not expired and the address matches
        return isNotExpired && isAddressMatch;
    };

    // Get token
    const getToken = (): string | null => {
        const storedData = storage.get(AUTH_TOKEN_KEY) as
            | AuthTokenData
            | undefined;
        return storedData && storedData.token ? storedData.token : null;
    };

    // Get userId
    const getUserId = (): string | null => {
        const storedData = storage.get(AUTH_TOKEN_KEY) as
            | AuthTokenData
            | undefined;
        return storedData && storedData.userId ? storedData.userId : null;
    };

    // Save token and userId
    const saveTokenAndUserId = (
        token: string,
        expiry: number,
        walletAddress: string,
        userId: string,
    ) => {
        storage.set(AUTH_TOKEN_KEY, {
            token,
            expiry,
            address: walletAddress,
            userId,
        });
        setAuthToken(token);
        setUserId(userId);
    };

    // Handle signature success
    const handleSignSuccess = (token: string, userId: string) => {
        if (address) {
            saveTokenAndUserId(
                token,
                Date.now() + 7 * 24 * 60 * 60 * 1000 - 60 * 60 * 1000,
                address,
                userId,
            ); // 7 days expired
        }
        setShowSignatureModal(false);
    };

    // Show signature modal
    const openSignatureModal = () => {
        setShowSignatureModal(true);
    };

    // Close signature modal
    const closeSignatureModal = () => {
        setShowSignatureModal(false);
    };

    // When the connection status or address changes, check if a signature is needed
    useEffect(() => {
        if (isConnected && address) {
            // If a wallet is connected, check if there is a valid token
            if (!isTokenValid()) {
                // If there is no valid token or the address does not match, open the signature modal
                openSignatureModal();
            } else {
                // If there is a valid token, set the token
                setAuthToken(getToken());
                setUserId(getUserId());
            }
        } else {
            // If a wallet is not connected, close the signature modal and clear the token
            closeSignatureModal();
            setAuthToken(null);
            setUserId(null);
        }
    }, [isConnected, address]);

    return {
        showSignatureModal,
        authToken,
        openSignatureModal,
        closeSignatureModal,
        handleSignSuccess,
    };
}
